// Configuracoes > WhatsApp — os celulares da loja num lugar so (DA-510).
//
// Substituiu os dois itens soltos do menu ("Conexao Baileys (Compras)" e
// "(Atendimento)"), no mesmo movimento que o ModalChatAtendimento fez em 06/2026.
// O que nasce aqui e' o que os dois itens antigos nao tinham: QUEM ENVIA os
// documentos da loja, escolhivel sem deploy.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Send, Trash2, ChevronDown, ChevronRight, AlertCircle, QrCode } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { Interruptor } from '../../../components/Interruptor';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import type { ChipWhatsApp, Inbox } from '../lib/tipos';
import { ModalConexaoBaileys } from './ModalConexaoBaileys';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

const CAIXAS: Array<{ id: Inbox; nome: string; explica: string }> = [
  {
    id: 'atendimento',
    nome: 'Atendimento',
    explica: 'A caixa dos clientes. Por ela saem orcamento, nota fiscal, comprovante, extrato e recibo.',
  },
  {
    id: 'compras',
    nome: 'Compras',
    explica: 'A caixa dos fornecedores, no celular do Ruan.',
  },
];

/**
 * Numero DISCAVEL a partir do que o WhatsApp gravou na credencial.
 *
 * ⚠️ O jid guarda celular brasileiro **sem o nono digito** (`556499561692`, 12
 * digitos), e o discavel tem 13 (`5564999561692`). Exibir cru parece inofensivo
 * e nao e': quem copia da tela pra ligar ou pra montar um `wa.me` leva um numero
 * que nao existe. Essa mesma confusao quase publicou um numero invalido na
 * vitrine em 09/08 ([[reference_numero_atendimento]]). A regra de repor o 9 e' a
 * mesma do `altBrazilJid` (`apps/api/src/lib/phone.ts`).
 */
export function exibirNumero(n: string | null): string | null {
  if (!n) return null;
  // ⚠️ Cortar o sufixo ANTES de limpar. O jid vem `556499561692:4@s.whatsapp.net`
  // e o `:4` e' o numero do APARELHO vinculado — jogar tudo num `replace(/\D/g)`
  // gruda esse digito no telefone, o numero fica com 13 e passa por "ja tem o
  // nono digito". Resultado: o 9 nao e' reposto e a tela mostra outro numero.
  let d = (n.split(':')[0] ?? '').split('@')[0]?.replace(/\D/g, '') ?? '';
  if (!d.startsWith('55')) return d;
  if (d.length === 12) d = d.slice(0, 4) + '9' + d.slice(4);
  if (d.length !== 13) return d;
  return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
}

export function ModalWhatsApp({ aberto, onFechar }: Props) {
  const toast = useToast();
  const [chips, setChips] = useState<ChipWhatsApp[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [confirmaRemover, setConfirmaRemover] = useState<string | null>(null);
  const [formAberto, setFormAberto] = useState(false);
  const [novoRotulo, setNovoRotulo] = useState('');
  const [novoInbox, setNovoInbox] = useState<Inbox>('atendimento');

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const r = await atendimentoApi.chipsListar();
      setChips(r.chips);
    } catch {
      toast.erro('Falha ao carregar os celulares');
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [toast]);

  useEffect(() => {
    if (aberto) void carregar();
  }, [aberto, carregar]);

  async function agir(instancia: string, fn: () => Promise<unknown>, okMsg: string) {
    setOcupado(instancia);
    try {
      await fn();
      toast.sucesso(okMsg);
      await carregar(true);
    } catch (e: any) {
      // O backend recusa com a razao escrita (celular desligado, nao pareado, e o
      // ultimo remetente da caixa). Mostrar o texto dele, nao um generico.
      toast.erro(e?.message || 'Nao deu certo');
    } finally {
      setOcupado(null);
    }
  }

  async function criar() {
    const rotulo = novoRotulo.trim();
    if (!rotulo) {
      toast.erro('De um nome pro celular');
      return;
    }
    setOcupado('__novo__');
    try {
      await atendimentoApi.chipCriar({ rotulo, inbox: novoInbox });
      setNovoRotulo('');
      setFormAberto(false);
      toast.sucesso('Celular criado. Ligue ele e pareie pelo QR.');
      await carregar(true);
    } catch (e: any) {
      toast.erro(e?.message || 'Falha ao criar');
    } finally {
      setOcupado(null);
    }
  }

  return (
    // telaCheia (DA-456): aqui nao e' um dialogo de uma decisao — e' a bancada dos celulares
    // da loja, com QR aberto, estado de conexao e a lista inteira ao mesmo tempo. Na caixa de
    // 760px o QR de um chip empurrava os outros pra fora da vista.
    <Modal aberto={aberto} onFechar={onFechar} titulo="WhatsApp" telaCheia
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      {carregando ? (
        <div className="flex items-center justify-center gap-2 py-10 opacity-80">
          <Loader2 size={18} className="animate-spin" /> Carregando...
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {CAIXAS.map((caixa) => {
            const daCaixa = chips.filter((c) => c.inbox === caixa.id);
            if (!daCaixa.length) return null;
            const remetente = daCaixa.find((c) => c.principal);
            return (
              <section key={caixa.id}>
                <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Caixa {caixa.nome}</h3>
                <p className="text-xs opacity-70" style={{ marginBottom: 10 }}>{caixa.explica}</p>

                {caixa.id === 'atendimento' && remetente && remetente.instancia !== 'atendimento' && (
                  // Sinal do RDS 1.26 item 3: enquanto um reserva fala, o cliente
                  // recebe de um numero que ele nao conhece — e o automatico tem
                  // que ficar desligado. O sistema nao esconde o estado.
                  <div className="flex gap-2 items-start p-3 rounded-lg" style={{ backgroundColor: '#f97316', border: '1px solid #c2410c', color: '#000', marginBottom: 10 }}>
                    <AlertCircle size={18} style={{ color: '#000' }} className="flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Quem esta falando nao e o numero oficial da loja</div>
                      <div className="text-xs mt-1">
                        O cliente vai receber os documentos de <b>{remetente.rotulo}</b>, um numero que
                        ele pode nao conhecer, e a resposta dele cai aqui mesmo. Serve pra atravessar
                        uma indisponibilidade — volte pro oficial assim que ele estiver de pe.
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {daCaixa.map((c) => (
                    <LinhaChip
                      key={c.instancia}
                      chip={c}
                      ocupado={ocupado === c.instancia}
                      expandido={expandido === c.instancia}
                      confirmandoRemover={confirmaRemover === c.instancia}
                      onExpandir={() => setExpandido(expandido === c.instancia ? null : c.instancia)}
                      onLigar={(v) => agir(c.instancia, () => atendimentoApi.chipLigar(c.instancia, v), v ? 'Ligado' : 'Desligado')}
                      onEnviarPor={() => agir(c.instancia, () => atendimentoApi.chipDefinirRemetente(c.instancia), `Os documentos da loja agora saem por ${c.rotulo}`)}
                      onPedirRemover={() => setConfirmaRemover(c.instancia)}
                      onCancelarRemover={() => setConfirmaRemover(null)}
                      onRemover={() => { setConfirmaRemover(null); void agir(c.instancia, () => atendimentoApi.chipRemover(c.instancia), 'Celular removido'); }}
                      onMudou={() => void carregar(true)}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Acao que acrescenta LINHA mora no pe da lista, e o rotulo nomeia o objeto. */}
          {!formAberto ? (
            <button type="button" className="btn-secundario"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto', alignSelf: 'flex-start' }}
              onClick={() => setFormAberto(true)}>
              <Plus size={15} /> Adicionar celular
            </button>
          ) : (
            <div className="p-3 rounded-lg" style={{ border: '1px solid var(--border)' }}>
              <div className="flex gap-2 flex-wrap items-end">
                <label className="text-xs" style={{ flex: '1 1 200px' }}>
                  Nome do celular
                  <input value={novoRotulo} onChange={(e) => setNovoRotulo(e.target.value)}
                    placeholder="Ex: Reserva Vivo" maxLength={60} style={{ width: '100%' }} />
                </label>
                <label className="text-xs" style={{ flex: '0 0 160px' }}>
                  Caixa
                  <select value={novoInbox} onChange={(e) => setNovoInbox(e.target.value as Inbox)} style={{ width: '100%' }}>
                    <option value="atendimento">Atendimento</option>
                    <option value="compras">Compras</option>
                  </select>
                </label>
                <button type="button" className="btn-primario" disabled={ocupado === '__novo__'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto' }}
                  onClick={() => void criar()}>
                  {ocupado === '__novo__' ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Criar
                </button>
                <button type="button" className="btn-secundario" style={{ flex: '0 0 auto' }}
                  onClick={() => { setFormAberto(false); setNovoRotulo(''); }}>Cancelar</button>
              </div>
              <p className="text-xs opacity-70" style={{ marginTop: 8 }}>
                O celular nasce desligado. Depois de criar: ligue, abra a conexao e escaneie o QR.
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function LinhaChip({
  chip, ocupado, expandido, confirmandoRemover,
  onExpandir, onLigar, onEnviarPor, onPedirRemover, onCancelarRemover, onRemover, onMudou,
}: {
  chip: ChipWhatsApp;
  ocupado: boolean;
  expandido: boolean;
  confirmandoRemover: boolean;
  onExpandir: () => void;
  onLigar: (v: boolean) => void;
  onEnviarPor: () => void;
  onPedirRemover: () => void;
  onCancelarRemover: () => void;
  onRemover: () => void;
  onMudou: () => void;
}) {
  const numero = exibirNumero(chip.numero);
  const noAr = chip.estado === 'open';

  return (
    <div className="rounded-lg" style={{ border: '1px solid var(--border)', padding: 12 }}>
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={onExpandir} className="btn-icon" aria-label="Abrir conexao"
          style={{ flex: '0 0 auto' }}>
          {expandido ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div style={{ flex: '1 1 180px', minWidth: 140 }}>
          <div style={{ fontWeight: 600 }}>{chip.rotulo}</div>
          <div className="text-xs" style={{ opacity: 0.65, fontSize: 11 }}>
            {numero ?? 'nunca pareado'}
          </div>
        </div>

        {/* Destaque positivo padrao (amarelo + preto). Nesta tela o amarelo nao
            significa mais nada, entao ele fica com a informacao que decide. */}
        {chip.principal && (
          <span style={{
            backgroundColor: '#fde68a', color: '#000', fontWeight: 700, fontSize: 11,
            padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap', flex: '0 0 auto',
          }}>
            ENVIA OS DOCUMENTOS
          </span>
        )}

        <EstadoChip chip={chip} />

        <div style={{ flex: '0 0 auto' }}>
          <Interruptor
            ligado={chip.ativo}
            desabilitado={ocupado}
            onChange={onLigar}
            rotuloLigado="Ligado"
            rotuloDesligado="Desligado"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
        {/* A PORTA do QR. Nasceu so como o chevron da esquerda e o Ruan precisou
            perguntar onde clicava — controle de grade nasce com fundo solido, e
            chevron sozinho e o "nao era discreto, era invisivel" da DA-353.
            Celular nunca pareado poe o parear como acao primaria: e' o unico
            passo que exige o aparelho na mao, entao ele manda na linha. */}
        <button type="button"
          className={chip.pareado ? 'btn-secundario' : 'btn-primario'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto', fontSize: 13 }}
          onClick={onExpandir}
          title={chip.pareado ? 'Abre o estado da conexao deste celular' : 'Abre o QR pra escanear com o celular'}>
          <QrCode size={14} /> {expandido ? 'Fechar conexao' : chip.pareado ? 'Conexao' : 'Parear celular'}
        </button>
        {!chip.principal && (
          <button type="button" className="btn-primario" disabled={ocupado || !noAr || !chip.ativo}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto', fontSize: 13 }}
            onClick={onEnviarPor}
            title={!noAr ? 'Pareie e conecte este celular antes de escolher ele' : 'Os documentos da loja passam a sair por este celular'}>
            <Send size={14} /> Enviar por este
          </button>
        )}
        {!chip.principal && !chip.pareado && (
          confirmandoRemover ? (
            <>
              <span className="text-xs" style={{ color: '#b91c1c' }}>Remover da lista?</span>
              <button type="button" className="btn-secundario text-xs" onClick={onCancelarRemover}>Cancelar</button>
              <button type="button" className="btn-perigo text-xs" onClick={onRemover}>Remover</button>
            </>
          ) : (
            <button type="button" className="btn-secundario" disabled={ocupado}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto', fontSize: 13 }}
              onClick={onPedirRemover}>
              <Trash2 size={14} /> Remover
            </button>
          )
        )}
      </div>

      {expandido && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--page-bg)' }}>
          <ModalConexaoBaileys embutido chip={chip.instancia} rotulo={chip.rotulo} pareado={chip.pareado} aoMudar={onMudou} />
        </div>
      )}
    </div>
  );
}

/**
 * Estado do aparelho. Sem verde/vermelho (daltonismo) e sem disputar com o
 * amarelo, que nesta tela significa "e este que envia".
 */
function EstadoChip({ chip }: { chip: ChipWhatsApp }) {
  const base = {
    fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
    whiteSpace: 'nowrap' as const, flex: '0 0 auto',
  };
  if (chip.estado === 'open') {
    return <span style={{ ...base, backgroundColor: 'var(--primary)', color: '#fff' }}>NO AR</span>;
  }
  if (chip.estado === 'connecting') {
    return <span style={{ ...base, backgroundColor: 'var(--page-bg)', color: 'var(--text-color)' }}>CONECTANDO</span>;
  }
  if (chip.estado === 'qr') {
    return <span style={{ ...base, backgroundColor: '#f97316', color: '#000' }}>ESCANEIE O QR</span>;
  }
  // Fora do ar: laranja (atencao, nao alarme). "Nunca pareado" e' outra coisa de
  // "caiu" — o primeiro exige o celular na mao, o segundo religa sozinho.
  return (
    <span style={{ ...base, backgroundColor: '#f97316', color: '#000' }}>
      {chip.pareado ? 'FORA DO AR' : 'NUNCA PAREADO'}
    </span>
  );
}
